/*
  Subscription rewards: product panel (box builder in a drawer) + cart drawer behaviour.
  Test theme #191139283268, 2026-09-13.

  Product page: segmented control (Abonnér | Engangskøb), a per-set price and a reward rail;
  "Byg din boks" opens a <dialog> drawer: stacked style cards with length chips (tap = +1,
  badge + minus; several lengths per style), delivery chips, gift unlock strip, total + add. A gift popup shows when a qualifying cart
  is missing the gifts.
  Ladder (box value = cluster subscription lines in the cart + the box being built, full price) — live numbers
  come from the app (window.CosneticLadder, see below); these are the fallback values:
    any -> 10% · 150 kr -> +fri fragt · 400 kr -> 20% + velkomstgave · 600 kr -> 40% + gave (Lilac St. offer in DKK, 2026-09-13)
  One-time: full price, no offer.
  Prices shown are a preview; checkout pricing comes from automatic Shopify discounts
  (OS/brands/COSNETIC/shopify/subrewards-discounts.json) and the Cosnetic Subscription Gift app
  (keep ELIGIBLE / GIFT_VARIANTS / GIFT_PROP in sync with its function).

  Cart updates follow the theme's own pattern: bundle the drawer section into the cart
  request, then dispatch `cart:change` so the drawer re-renders itself.
*/
(() => {
  // Ladder numbers come from the app: shop metafield cosnetic.subscription_ladder, printed as
  // window.CosneticLadder in layout/theme.liquid and rebuilt from the live Shopify discounts and the gift
  // settings by the app's Kampagner page. The literals below are only the fallback when that data is missing.
  const LADDER = window.CosneticLadder || {};
  const MAX_PER_LENGTH = 20;
  const GIFT_PROP = '_subrewards_gift';
  const ore = (kr) => Math.round(Number(kr) * 100);
  const idList = (list, fallback) => (Array.isArray(list) && list.length ? list.map(Number) : fallback);
  const SHIP_BOX = LADDER.shippingBox != null ? ore(LADDER.shippingBox) : 15000;

  // Rail stops = every discount step plus the free-shipping stop, with the % in force at each stop.
  const buildRule = (key, fallback) => {
    const data = LADDER[key] || {};
    const steps = (Array.isArray(data.steps) && data.steps.length ? data.steps : fallback.steps).map((step) => ({ min: ore(step.min), pct: Number(step.percent) }));
    const giftMin = data.giftEnabled === false ? Infinity : data.giftMin != null ? ore(data.giftMin) : 40000;
    const stops = steps.map((step) => ({ ...step, ship: SHIP_BOX > 0 && step.min === SHIP_BOX }));
    if (SHIP_BOX > 0 && !stops.some((stop) => stop.ship)) stops.push({ min: SHIP_BOX, pct: null, ship: true });
    stops.sort((a, b) => a.min - b.min);
    let pct = steps[0].pct;
    stops.forEach((stop) => { if (stop.pct == null) stop.pct = pct; else pct = stop.pct; });
    return {
      eligible: new Set(idList(data.productIds, fallback.eligible)),
      gifts: idList(data.giftVariantIds, fallback.gifts),
      giftMode: fallback.giftMode,
      giftMin,
      tiers: stops.map((stop) => stop.min),
      pcts: stops.map((stop) => stop.pct),
      labels: stops.slice(1).map((stop) => {
        if (stop.ship && !steps.some((step) => step.min === stop.min)) return 'fri fragt';
        return `${stop.pct}% rabat${stop.min === giftMin ? ` + ${fallback.giftLabel}` : ''}${stop.ship ? ' + fri fragt' : ''}`;
      }),
    };
  };
  const FALLBACK_STEPS = [{ min: 0, percent: 10 }, { min: 400, percent: 20 }, { min: 600, percent: 40 }];
  // Lashes and nails each have their own box. Nails: ONE free set of their choice.
  const RULES = {
    lashes: buildRule('lashes', {
      steps: FALLBACK_STEPS,
      eligible: [15532471484740, 15532470763844, 15532470960452, 15532470337860, 8543196053828, 8543196283204, 9086849352004], // clusters + accessories on subscription
      gifts: [46730034020676, 46730036543812, 34801156980869],
      giftMode: 'each',
      giftLabel: '3 gratis gaver',
    }),
    nails: buildRule('nails', {
      steps: FALLBACK_STEPS,
      eligible: [15182808219972, 15182813266244, 15182815461700, 15182816969028],
      gifts: [55209233383748, 55209247834436, 55209254453572, 55209257861444],
      giftMode: 'choose',
      giftLabel: 'gratis neglesæt',
    }),
  };
  const TOP_PCT = Math.max(...RULES.lashes.pcts, ...RULES.nails.pcts);
  const GIFT_DISMISS_KEY = 'subrewards-gift-popup-dismissed';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Shop money format has no decimals, so format øre by hand (67,50 kr, not 68 kr).
  // Køb 3, betal for 2 (one-time): of every three sets the cheapest is free. Matches the automatic
  // BXGY discounts "Køb 3, betal for 2" (lashes) and "Køb 3, betal for 2 – negle".
  const threeForTwo = (unitPrices) => {
    const sorted = [...unitPrices].sort((a, b) => a - b);
    const free = sorted.slice(0, Math.floor(sorted.length / 3)).reduce((sum, price) => sum + price, 0);
    return sorted.reduce((sum, price) => sum + price, 0) - free;
  };
  const threeForTwoHint = (sets, kind = 'lashes') => {
    const free = Math.floor(sets / 3);
    const rest = sets % 3;
    if (!sets) return `Køb 3, betal for 2 – mix ${kind === 'nails' ? 'designs' : 'længder'} frit`;
    if (rest === 2) return 'Tilføj 1 sæt mere → det er gratis';
    if (rest === 1) return free ? `${free} gratis ✓ · tilføj 2 mere → 1 gratis til` : 'Tilføj 2 sæt mere → det 3. er gratis';
    return `${free} sæt gratis ✓`;
  };

  const formatMoney = (cents) => {
    const value = Math.max(0, Math.round(cents));
    const kr = Math.floor(value / 100);
    const ore = value % 100;
    return `${kr.toLocaleString('da-DK')}${ore ? ',' + String(ore).padStart(2, '0') : ''} kr`;
  };

  const percentFor = (box, rule = RULES.lashes) => rule.tiers.reduce((pct, min, i) => (box >= min ? rule.pcts[i] : pct), rule.pcts[0]);

  // Fill (0-100) of a reward rail whose stops sit evenly spaced along the bar.
  const railProgress = (value, stops) => {
    const last = stops.length - 1;
    if (value >= stops[last]) return 100;
    if (value < stops[0]) return 0;
    for (let i = 0; i < last; i++) {
      if (value < stops[i + 1]) return ((i + (value - stops[i]) / (stops[i + 1] - stops[i])) / last) * 100;
    }
    return 100;
  };

  const nextStepFor = (box, rule = RULES.lashes) => {
    for (let i = 1; i < rule.tiers.length; i++) {
      if (box < rule.tiers[i]) return { amount: rule.tiers[i] - box, label: rule.labels[i - 1] };
    }
    return null;
  };

  const boxValue = (cart, rule = RULES.lashes) =>
    cart.items.reduce(
      (sum, item) => (item.selling_plan_allocation && rule.eligible.has(item.product_id) ? sum + item.original_line_price : sum),
      0
    );

  const isGift = (item, rule = RULES.lashes) => !!(item.properties && item.properties[GIFT_PROP] && rule.gifts.includes(item.variant_id));

  const missingGifts = (cart, rule = RULES.lashes, chosen = null) => {
    const present = cart.items.filter((item) => isGift(item, rule));
    const line = (id) => ({ id, quantity: 1, properties: { [GIFT_PROP]: '1' } });
    if (rule.giftMode === 'choose') return present.length ? [] : [line(chosen || rule.gifts[0])];
    const have = new Set(present.map((item) => item.variant_id));
    return rule.gifts.filter((id) => !have.has(id)).map(line);
  };

  const storage = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (error) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (error) { /* private mode */ }
    },
  };

  let lastCart = null;

  const fetchCart = async () => (await fetch(`${ROOT}cart.js`)).json();

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  // `variant:add` as baseEvent makes the drawer swap its content immediately (no 1.25s delay).
  const announce = (cart) =>
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );

  const postJSON = async (path, body) => {
    const response = await fetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || 'Der opstod en fejl. Prøv igen.');
    return json;
  };

  // `beforeAnnounce` runs once the items are in the cart, before the theme opens its cart drawer.
  const addItems = async (items, source, beforeAnnounce) => {
    const added = await postJSON('cart/add.js', { items, sections: bundledSections().join(',') });
    const cart = await fetchCart();
    cart.sections = added.sections;
    if (beforeAnnounce) beforeAnnounce();
    if (source) {
      source.dispatchEvent(
        new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart } })
      );
    }
    announce(cart);
    return cart;
  };

  const addMissingGifts = async (rule = RULES.lashes, chosen = null, beforeAnnounce) => {
    const items = missingGifts(await fetchCart(), rule, chosen);
    if (items.length) await addItems(items, null, beforeAnnounce);
    else if (beforeAnnounce) beforeAnnounce();
  };

  const changeLine = async (body) => {
    const cart = await postJSON('cart/change.js', { ...body, sections: bundledSections() });
    announce(cart);
    return cart;
  };

  const updateLines = async (updates) => {
    const cart = await postJSON('cart/update.js', { updates, sections: bundledSections() });
    announce(cart);
    return cart;
  };

  // Gifts only belong in a cart whose box (per rule) reaches the gift step: lashes one of each,
  // nails a single chosen set.
  let syncingGifts = false;
  const syncGifts = async (cart) => {
    if (syncingGifts || !cart || !cart.items) return;
    const updates = {};
    Object.values(RULES).forEach((rule) => {
      const gifts = cart.items.filter((item) => isGift(item, rule));
      if (!gifts.length) return;
      const box = boxValue(cart, rule);
      const seen = new Set();
      gifts.forEach((item, index) => {
        const extra = rule.giftMode === 'choose' ? index > 0 : seen.has(item.variant_id);
        if (box < rule.giftMin || extra) updates[item.key] = 0;
        else if (item.quantity > 1) updates[item.key] = 1;
        seen.add(item.variant_id);
      });
    });
    if (!Object.keys(updates).length) return;

    syncingGifts = true;
    try {
      await updateLines(updates);
    } catch (error) {
      console.error('[subrewards]', error);
    } finally {
      syncingGifts = false;
    }
  };

  const refreshPanels = (cart, fromChange) =>
    document.querySelectorAll('subrewards-panel').forEach((panel) => panel.setCart && panel.setCart(cart, fromChange));

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) return;
    lastCart = cart;
    refreshPanels(cart, true);
    syncGifts(cart);
  });

  // Cart drawer: add the welcome gift
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-subrewards-add-gifts]');
    if (!button) return;
    event.preventDefault();
    button.setAttribute('aria-busy', 'true');
    try {
      await addMissingGifts(RULES[button.dataset.kind] || RULES.lashes, Number(button.dataset.variantId) || null);
    } catch (error) {
      console.error('[subrewards]', error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  // Cart drawer: change delivery interval on a subscription line
  document.addEventListener('change', async (event) => {
    const select = event.target.closest('select[data-subrewards-line-plan]');
    if (!select) return;
    select.disabled = true;
    try {
      await changeLine({
        id: select.dataset.lineKey,
        quantity: parseInt(select.dataset.quantity, 10),
        selling_plan: parseInt(select.value, 10),
      });
    } catch (error) {
      console.error('[subrewards]', error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  });

  const setRadio = (buttons, isSelected, attribute = 'aria-checked') =>
    buttons.forEach((button) => {
      const selected = isSelected(button);
      button.classList.toggle(attribute === 'aria-pressed' ? 'is-active' : 'is-selected', selected);
      button.setAttribute(attribute, String(selected));
    });

  class SubrewardsPanel extends HTMLElement {
    connectedCallback() {
      this.productId = Number(this.dataset.productId);
      this.variantId = Number(this.dataset.variantId);
      this.price = Number(this.dataset.price);
      this.mode = this.dataset.mode || 'subscription';
      this.cartBox = 0;
      this.touched = false;
      this.rule = RULES[this.dataset.kind] || RULES.lashes;
      this.returning = this.dataset.returning === 'true';
      if (this.returning) this.querySelectorAll('[data-sr-gifts], [data-sr-gift-teaser]').forEach((el) => el.remove());
      this.onceQty = 1;

      this.drawer = this.querySelector('[data-sr-drawer]');
      this.giftModal = this.querySelector('[data-sr-gift-modal]');
      this.planSelect = this.querySelector('[data-subrewards-plan]');
      this.cta = this.querySelector('[data-subrewards-add]');
      this.ctaLabel = this.querySelector('[data-sr-cta-label]');
      this.errorEl = this.querySelector('[data-subrewards-error]');

      this.querySelectorAll('[data-sr-money]').forEach((el) => {
        el.textContent = formatMoney(Number(el.dataset.srMoney));
      });

      this._onClick = this._onClick.bind(this);
      this._onBackdrop = (event) => {
        if (event.target === event.currentTarget) event.currentTarget.close();
      };
      this._onVariantChange = (event) => {
        const detail = event.detail;
        if (!detail || !detail.variant || !detail.product || Number(detail.product.id) !== this.productId) return;
        this.variantId = Number(detail.variant.id);
        this.price = Number(detail.variant.price);
        // Until the shopper touches the builder, the box follows the variant picked on the page.
        if (!this.touched) {
          const len = this._lenFor(this.variantId);
          if (len && !len.classList.contains('is-soldout')) {
            this.lens.forEach((el) => this._setCount(el, el === len ? 1 : 0));
          }
        }
        this.render();
      };

      this.addEventListener('click', this._onClick);
      [this.drawer, this.giftModal].forEach((dialog) => dialog && dialog.addEventListener('click', this._onBackdrop));
      document.addEventListener('variant:change', this._onVariantChange);

      this.render();
      fetchCart()
        .then((cart) => {
          lastCart = cart;
          this.setCart(cart, false);
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    get lens() {
      return [...this.querySelectorAll('[data-sr-len]')];
    }

    get picked() {
      return this.lens
        .map((el) => ({
          variantId: Number(el.dataset.variantId),
          productId: Number(el.dataset.productId),
          price: Number(el.dataset.price),
          count: Number(el.dataset.count) || 0,
        }))
        .filter((item) => item.count > 0);
    }

    // Accessories added to the lash subscription: full price, don't count toward the box/tiers.
    get addons() {
      return [...this.querySelectorAll('[data-sr-addon]')]
        .map((el) => ({ el, variantId: Number(el.dataset.variantId), price: Number(el.dataset.price), count: Number(el.dataset.count) || 0 }))
        .filter((item) => item.count > 0);
    }

    // Accessories added to a one-time purchase on the product page (full price).
    get onceAddons() {
      return [...this.querySelectorAll('[data-sr-once-addon]')]
        .map((el) => ({ variantId: Number(el.dataset.variantId), price: Number(el.dataset.price), count: Number(el.dataset.count) || 0 }))
        .filter((item) => item.count > 0);
    }

    _lenFor(variantId) {
      return this.lens.find((el) => Number(el.dataset.variantId) === variantId);
    }

    _setCount(len, count) {
      const next = Math.min(MAX_PER_LENGTH, Math.max(0, count));
      len.dataset.count = String(next);
      len.classList.toggle('is-picked', next > 0);
      const label = len.querySelector('[data-sr-len-count]');
      if (label) label.textContent = String(next);
    }

    setCart(cart, fromChange) {
      this.cartBox = boxValue(cart, this.rule);
      this.render();
      if (fromChange) this._maybeShowGiftPopup(cart);
    }

    _maybeShowGiftPopup(cart) {
      if (this.returning) return;
      if (!this.giftModal || this.giftModal.open || this.adding) return;
      if (this.drawer && this.drawer.open) return;
      if (storage.get(GIFT_DISMISS_KEY)) return;
      if (boxValue(cart, this.rule) < this.rule.giftMin || !missingGifts(cart, this.rule, this._chosenGift()).length) return;
      this.giftModal.showModal();
    }

    openDrawer() {
      if (this.drawer && !this.drawer.open) this.drawer.showModal();
      this.render();
    }

    closeDrawer() {
      if (this.drawer && this.drawer.open) this.drawer.close();
    }

    _setText(selector, text) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    }

    _setHidden(selector, hidden) {
      this.querySelectorAll(selector).forEach((el) => {
        el.hidden = hidden;
      });
    }

    render() {
      const subscribing = this.mode === 'subscription';
      const picked = this.picked;
      const sets = picked.reduce((sum, item) => sum + item.count, 0);
      const value = picked.reduce((sum, item) => sum + item.count * item.price, 0);
      const unit = this.price || 7500;

      this.dataset.mode = this.mode;
      this.querySelectorAll('[data-sr-show]').forEach((el) => {
        el.hidden = el.dataset.srShow !== this.mode;
      });
      setRadio(this.querySelectorAll('[data-sr-mode]'), (tab) => tab.dataset.srMode === this.mode);

      // style cards: "2 valgt"
      this.querySelectorAll('[data-sr-style]').forEach((card) => {
        const count = picked
          .filter((item) => item.productId === Number(card.dataset.productId))
          .reduce((sum, item) => sum + item.count, 0);
        card.classList.toggle('is-picked', count > 0);
        const badge = card.querySelector('[data-sr-style-count]');
        badge.hidden = count === 0;
        badge.textContent = `${count} valgt`;
      });

      let now;
      let perSet;
      let next;
      let box = 0;
      if (subscribing) {
        // accessories on subscription count in the box and get the same tier %
        const accessoryValue = this.addons.reduce((sum, item) => sum + item.count * item.price, 0);
        box = this.cartBox + value + accessoryValue;
        const pct = percentFor(box, this.rule);
        now = Math.round(((value + accessoryValue) * (100 - pct)) / 100);
        perSet = Math.round((unit * (100 - pct)) / 100);
  
        const step = nextStepFor(box, this.rule);
        next = step
          ? `Tilføj ${Math.max(1, Math.ceil(step.amount / unit))} sæt mere → ${step.label}`
          : 'Du har den laveste pris ✓';

        const unlocked = box >= this.rule.giftMin;
        const gifts = this.querySelector('[data-sr-gifts]');
        if (gifts) gifts.classList.toggle('is-unlocked', unlocked);
        this._setText('[data-sr-gift-tag]', unlocked ? 'Gratis' : 'Låst');
        this._setText(
          '[data-sr-gift-status]',
          unlocked
            ? 'Låst op ✓ – lægges automatisk i kurven'
            : `Tilføj ${Math.max(1, Math.ceil((this.rule.giftMin - box) / unit))} sæt mere for at låse op`
        );
      } else {
        // one-time: full price, no offer (Martin 2026-09-13)
        now = threeForTwo(picked.flatMap((item) => Array(item.count).fill(item.price)));
        perSet = sets ? Math.round(now / sets) : unit;
        next = threeForTwoHint(sets, this.dataset.kind);
      }

      // reward rails
      this.querySelectorAll('[data-sr-rail]').forEach((rail) => {
        const railValue = rail.dataset.srRail === 'subscription' ? box : sets;
        const stops = [...rail.querySelectorAll('[data-sr-stop]')];
        const values = stops.map((stop) => Number(stop.dataset.srStop));
        stops.forEach((stop, i) => stop.classList.toggle('is-done', railValue >= values[i]));
        rail.querySelector('[data-sr-rail-fill]').style.width = `${railProgress(railValue, values)}%`;
      });

      // product page card: headline is always the cheapest price the offer reaches
      // (subscription 40% at 6 sets / one-time 3 for 2); the line below shows what they pay now.
      const best = subscribing
        ? { price: Math.round((unit * (100 - this.rule.pcts[this.rule.pcts.length - 1])) / 100), reached: box >= this.rule.tiers[this.rule.tiers.length - 1] }
        : { price: Math.ceil((unit * 2) / 3 / 100) * 100, reached: false }; // whole kr, rounded up
      this._setText('[data-sr-gift-min]', formatMoney(this.rule.giftMin));
      this._setText('[data-sr-per-set]', formatMoney(best.price));
      this._setHidden('[data-sr-from]', best.reached);
      this._setText('[data-sr-per-set-was]', formatMoney(unit));
      const topPct = this.rule.pcts[this.rule.pcts.length - 1];
      this._setText('[data-sr-save-tag]', subscribing ? `Spar op til ${topPct}%` : 'Køb 3, betal for 2');
      this._setText('[data-sr-best]', best.reached ? 'Laveste pris ✓' : `Nu ${formatMoney(perSet)}/sæt`);
      this._setHidden('[data-sr-best]', !subscribing);
      this._setHidden('[data-sr-per-set-was]', false);
      this._setText('[data-sr-next]', sets ? next : subscribing ? 'Tryk på en længde for at tilføje dit første sæt' : '');

      // drawer footer
      const addons = subscribing ? this.addons : [];
      const addonCount = addons.reduce((sum, item) => sum + item.count, 0);
      const addonValue = addons.reduce((sum, item) => sum + item.count * item.price, 0);
      this._setText('[data-sr-count]', addonCount ? `${sets} sæt + ${addonCount} tilbehør` : `${sets} sæt`);
      this._setText('[data-sr-total-now]', formatMoney(now));
      this._setText('[data-sr-total-was]', formatMoney(value + addonValue));
      this._setHidden('[data-sr-total-was]', value + addonValue === now);
      this._setText('[data-sr-save-amount]', `Du sparer ${formatMoney(value + addonValue - now)}`);
      this._setHidden('[data-sr-save-amount]', value + addonValue - now <= 0);

      // product page one-time: length chips + quantity, button adds straight to cart
      // product page one-time: a counter per length (starts at 0) + accessories; greyed out until something is added
      const onceEls = [...this.querySelectorAll('[data-sr-once-addon]')];
      const onceSetUnits = onceEls.filter((el) => el.closest('.sr-once__len')).flatMap((el) => Array(Number(el.dataset.count) || 0).fill(Number(el.dataset.price)));
      const onceFull = this.onceAddons.reduce((sum, item) => sum + item.count * item.price, 0);
      const onceSetsFull = onceSetUnits.reduce((sum, price) => sum + price, 0);
      const onceTotal = onceFull - (onceSetsFull - threeForTwo(onceSetUnits));
      this._setText('[data-sr-once-hint]', threeForTwoHint(onceSetUnits.length, this.dataset.kind));
      const pdpCta = this.querySelector('[data-sr-open]');
      if (pdpCta) {
        pdpCta.disabled = !subscribing && onceTotal === 0;
        this._setText('[data-sr-pdp-cta-label]', subscribing ? 'Byg din boks' : onceTotal ? `Tilføj til kurv · ${formatMoney(onceTotal)}` : 'Tilføj til kurv');
      }

      this.cta.disabled = sets === 0;
      this.ctaLabel.textContent = sets === 0 ? 'Vælg mindst 1 sæt' : subscribing ? 'Tilføj boks til kurv' : 'Læg i kurv';
    }

    _onClick(event) {
      const target = event.target;

      const modeTab = target.closest('[data-sr-mode]');
      if (modeTab) {
        this.mode = modeTab.dataset.srMode;
        this.render();
        return;
      }

      const onceVariant = target.closest('[data-sr-once-variant]');
      if (onceVariant && !onceVariant.disabled) {
        setRadio(this.querySelectorAll('[data-sr-once-variant]'), (chip) => chip === onceVariant);
        this.render();
        return;
      }

      const onceQty = target.closest('[data-sr-once-qty]');
      if (onceQty) {
        this.onceQty = Math.min(MAX_PER_LENGTH, Math.max(1, this.onceQty + Number(onceQty.dataset.srOnceQty)));
        this.render();
        return;
      }

      // Product page button: subscription opens the box builder, one-time adds straight to cart.
      if (target.closest('[data-sr-open]')) {
        if (this.mode === 'onetime') this._addOnce();
        else this.openDrawer();
        return;
      }

      if (target.closest('[data-sr-close]')) {
        this.closeDrawer();
        return;
      }

      const onceAddonStep = target.closest('[data-sr-once-addon-step]');
      if (onceAddonStep) {
        const addon = onceAddonStep.closest('[data-sr-once-addon]');
        const next = Math.min(MAX_PER_LENGTH, Math.max(0, (Number(addon.dataset.count) || 0) + Number(onceAddonStep.dataset.srOnceAddonStep)));
        addon.dataset.count = String(next);
        addon.classList.toggle('is-picked', next > 0);
        addon.querySelector('[data-sr-once-addon-count]').textContent = String(next);
        this.render();
        return;
      }

      const addonStep = target.closest('[data-sr-addon-step]');
      if (addonStep) {
        const addon = addonStep.closest('[data-sr-addon]');
        const next = Math.min(MAX_PER_LENGTH, Math.max(0, (Number(addon.dataset.count) || 0) + Number(addonStep.dataset.srAddonStep)));
        addon.dataset.count = String(next);
        addon.classList.toggle('is-picked', next > 0);
        addon.querySelector('[data-sr-addon-count]').textContent = String(next);
        this.render();
        return;
      }

      const step = target.closest('[data-sr-len-step]');
      if (step) {
        const len = step.closest('[data-sr-len]');
        this._setCount(len, (Number(len.dataset.count) || 0) + Number(step.dataset.srLenStep));
        this.touched = true;
        this.render();
        return;
      }

      const planChip = target.closest('[data-subrewards-plan-chip]');
      if (planChip) {
        setRadio(this.querySelectorAll('[data-subrewards-plan-chip]'), (chip) => chip === planChip);
        if (this.planSelect) this.planSelect.value = planChip.dataset.planId;
        return;
      }

      if (target.closest('[data-subrewards-add]')) {
        this._add();
        return;
      }

      const giftChoice = target.closest('[data-sr-gift-choice]');
      if (giftChoice) {
        const id = giftChoice.dataset.variantId;
        this.querySelectorAll('[data-sr-gift-choice]').forEach((el) => {
          const on = el.dataset.variantId === id;
          el.classList.toggle('is-selected', on);
          el.setAttribute('aria-pressed', String(on));
        });
        return;
      }

      const giftAdd = target.closest('[data-sr-gift-add]');
      if (giftAdd) {
        giftAdd.setAttribute('aria-busy', 'true');
        addMissingGifts(this.rule, this._chosenGift(), () => this.giftModal.close())
          .catch((error) => console.error('[subrewards]', error))
          .finally(() => giftAdd.removeAttribute('aria-busy'));
        return;
      }

      if (target.closest('[data-sr-gift-dismiss]')) {
        storage.set(GIFT_DISMISS_KEY, '1');
        this.giftModal.close();
      }
    }

    _chosenGift() {
      const chosen = this.querySelector('[data-sr-gift-choice].is-selected');
      return chosen ? Number(chosen.dataset.variantId) : null;
    }

    async _addOnce() {
      const button = this.querySelector('[data-sr-open]');
      const error = this.querySelector('[data-sr-pdp-error]');
      if (!this.onceAddons.length) return;
      error.hidden = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const items = this.onceAddons.map((item) => ({ id: item.variantId, quantity: item.count }));
        await addItems(items, this);
        this.querySelectorAll('[data-sr-once-addon]').forEach((el) => {
          el.dataset.count = '0';
          el.classList.remove('is-picked');
          el.querySelector('[data-sr-once-addon-count]').textContent = '0';
        });
        this.render();
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    async _add() {
      const picked = this.picked;
      if (!picked.length) return;
      this.errorEl.hidden = true;

      const subscribing = this.mode === 'subscription';
      const plan = subscribing && this.planSelect ? Number(this.planSelect.value) : null;
      const items = [...picked, ...(subscribing ? this.addons : [])].map((item) => {
        const line = { id: item.variantId, quantity: item.count };
        if (plan) line.selling_plan = plan;
        return line;
      });

      this.adding = true;
      this.cta.setAttribute('aria-busy', 'true');
      try {
        if (subscribing) {
          const value = [...picked, ...this.addons].reduce((sum, item) => sum + item.count * item.price, 0);
          const cart = await fetchCart();
          if (!this.returning && boxValue(cart, this.rule) + value >= this.rule.giftMin) items.push(...missingGifts(cart, this.rule, this._chosenGift()));
        }
        await addItems(items, this, () => this.closeDrawer());
      } catch (error) {
        this.errorEl.textContent = error.message;
        this.errorEl.hidden = false;
      } finally {
        this.adding = false;
        this.cta.removeAttribute('aria-busy');
      }
    }
  }

  // Loop's plan description (edited in the Loop admin, shared by every product) still says
  // "SPAR 45%" / "Op til 45% rabat". The subscription offer tops out at 40%, so correct the
  // text wherever Loop renders it (accessory product pages that still use the Loop widget).
  const LOOP_TEXT_FIXES = [
    [/SPAR\s*45\s*%/gi, `SPAR OP TIL ${TOP_PCT}%`],
    [/(op til )?45\s*% rabat/gi, `Op til ${TOP_PCT}% rabat`],
    ...(SHIP_BOX > 0 ? [[/Gratis fragt på alle ordrer/gi, `Gratis fragt fra ${SHIP_BOX / 100} kr`]] : []),
  ];
  const fixLoopText = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!/45\s*%|alle ordrer/i.test(node.textContent)) continue;
      let text = node.textContent;
      LOOP_TEXT_FIXES.forEach(([pattern, replacement]) => { text = text.replace(pattern, replacement); });
      if (text !== node.textContent) node.textContent = text;
    }
  };
  const watchLoopWidgets = () => {
    document.querySelectorAll('.loop-widget-container').forEach((widget) => {
      if (widget.dataset.srTextFix) return;
      widget.dataset.srTextFix = '1';
      fixLoopText(widget);
      new MutationObserver(() => fixLoopText(widget)).observe(widget, { childList: true, subtree: true, characterData: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchLoopWidgets);
  else watchLoopWidgets();
  new MutationObserver(watchLoopWidgets).observe(document.documentElement, { childList: true, subtree: true });

  if (!window.customElements.get('subrewards-panel')) {
    window.customElements.define('subrewards-panel', SubrewardsPanel);
  }

  // On load: clean up gifts left behind in a cart that no longer qualifies.
  fetchCart()
    .then((cart) => {
      lastCart = lastCart || cart;
      syncGifts(cart);
    })
    .catch(() => {});
})();
